import React from 'react';
import {
  Box,
  Pagination,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';

interface ApplicationPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

const ApplicationPagination: React.FC<ApplicationPaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (totalPages <= 1) {
    return null;
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        py: 3,
        px: 2,
      }}
    >
      {/* Page info */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          textAlign: 'center',
          fontWeight: 500,
        }}
      >
        Showing {startItem}-{endItem} of {totalItems} applications
      </Typography>

      {/* Pagination controls */}
      <Pagination
        count={totalPages}
        page={currentPage}
        onChange={(event, page) => onPageChange(page)}
        disabled={loading}
        color="primary"
        size={isMobile ? 'small' : 'medium'}
        sx={{
          '& .MuiPaginationItem-root': {
            borderRadius: 2,
            fontWeight: 600,
            minWidth: 40,
            height: 40,
            '&:hover': {
              backgroundColor: theme.palette.primary.light + '20',
            },
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
          },
          '& .MuiPaginationItem-previousNext': {
            borderRadius: 2,
            fontWeight: 600,
            minWidth: 40,
            height: 40,
            backgroundColor: theme.palette.grey[100],
            '&:hover': {
              backgroundColor: theme.palette.grey[200],
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.grey[50],
              color: theme.palette.grey[400],
            },
          },
        }}
        showFirstButton
        showLastButton
      />

      {/* Quick page jump for larger datasets */}
      {totalPages > 10 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Go to page:
          </Typography>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={currentPage}
            onChange={(e) => {
              const page = parseInt(e.target.value);
              if (page >= 1 && page <= totalPages) {
                onPageChange(page);
              }
            }}
            style={{
              width: 60,
              padding: '4px 8px',
              borderRadius: 8,
              border: `1px solid ${theme.palette.divider}`,
              textAlign: 'center',
              fontSize: '14px',
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ApplicationPagination;
